import { Component, computed, inject, signal } from '@angular/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { Assistant, AssistantType } from '@vendasta/ai-assistants';
import { ACCOUNT_GROUP_ID_TOKEN } from '../../../core/tokens';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { ImageUploadComponent } from '../image-upload/image-upload.component';
import { StickyFooterComponent } from '../../sticky-footer/sticky-footer.component';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { DEFAULT_AVATAR_SVG_ICON } from '../../../core/ai-assistant.constants';
import { PageService } from '@vendasta/galaxy/page/src/page.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { AiAssistantService } from '../../../core/services/ai-assistant.service';
import { CanComponentDeactivate } from '../../../core/services/unsaved-changes-v2.guard';
import { AiAssistantFormV2Service } from './ai-assistant-form-v2.service';

const FALLBACK_BACK_URL = '../..';

const BETA_ASSISTANT_TYPES = [AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST, AssistantType.ASSISTANT_TYPE_CUSTOM];

@Component({
  selector: 'ai-ai-assistant-form-v2',
  imports: [
    CommonModule,
    MatInputModule,
    MatCardModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    AiAssistantI18nModule,
    ImageUploadComponent,
    StickyFooterComponent,
    GalaxyPageModule,
    GalaxyAvatarModule,
    GalaxyTooltipModule,
    MatIconModule,
    GalaxyBadgeModule,
  ],
  providers: [AiAssistantFormV2Service],
  templateUrl: './ai-assistant-form-v2.component.html',
  styleUrl: './ai-assistant-form-v2.component.scss',
})
export class AiAssistantFormV2Component implements CanComponentDeactivate {
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly aiAssistantService = inject(AiAssistantService);
  private readonly route = inject(ActivatedRoute);
  private readonly snackbarService = inject(SnackbarService);
  private readonly pageService = inject(PageService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly aiIconService = inject(GalaxyAiIconService);
  private readonly router = inject(Router);
  private readonly aiAssistantFormV2Service = inject(AiAssistantFormV2Service);

  readonly defaultAvatarIcon = DEFAULT_AVATAR_SVG_ICON;

  protected assistantForm: FormGroup = this.formBuilder.group({
    name: [''],
    assistantAvatarUrl: [''],
  });

  protected readonly aiAssistant = toSignal(this.aiAssistantFormV2Service.aiAssistant$);

  protected readonly loading = computed(() => {
    return this.aiAssistant() === undefined;
  });

  protected readonly saving = signal(false);

  protected readonly showBetaBadge = computed(() => {
    const assistantType = this.aiAssistant()?.assistant?.type;
    return assistantType && BETA_ASSISTANT_TYPES.includes(assistantType);
  });

  constructor() {
    // Make sure the default AI icon is registered
    this.aiIconService.dummyInit();

    this.aiAssistantFormV2Service.aiAssistant$.pipe(takeUntilDestroyed()).subscribe((assistant) => {
      this.assistantForm.reset({
        name: assistant?.assistant.name || '',
        assistantAvatarUrl: assistant?.assistant.avatarUrl || '',
      });
    });
  }

  protected onImageChanged(imageUrl: string): void {
    this.assistantForm.get('assistantAvatarUrl')?.setValue(imageUrl);
    this.assistantForm.get('assistantAvatarUrl')?.markAsDirty();
  }

  protected async submit(): Promise<void> {
    try {
      this.saving.set(true);

      const aiAssistant = await firstValueFrom(this.aiAssistantFormV2Service.aiAssistant$);
      let assistant = aiAssistant?.assistant || new Assistant();

      const createNewAssistant = !assistant?.id;

      if (!this.assistantForm.dirty && !createNewAssistant) {
        return;
      }

      if (!this.assistantForm.valid) {
        this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.ASSISTANT_VALIDATION_ERROR');
        return;
      }

      if (createNewAssistant) {
        // Create a new default assistant first
        const newAiAssistant = await firstValueFrom(this.aiAssistantFormV2Service.upsertAssistant(assistant, true));
        assistant = newAiAssistant.assistant;
      }

      assistant.name = this.assistantForm.value.name;
      assistant.avatarUrl = this.assistantForm.value.assistantAvatarUrl;

      const newAiAssistant = await firstValueFrom(this.aiAssistantFormV2Service.upsertAssistant(assistant));

      this.assistantForm.markAsPristine();

      if (createNewAssistant) {
        const accountGroupId = await firstValueFrom(this.accountGroupId$);
        // Navigate to the edit page
        const url = this.aiAssistantService.buildAssistantConfigurationUrl(
          accountGroupId || '',
          newAiAssistant.assistant.id || '',
        );
        this.router.navigateByUrl(url, { replaceUrl: true });
      }
      this.snackbarService.openSuccessSnack('AI_ASSISTANT.SETTINGS.ASSISTANT_UPDATED');
    } catch (e) {
      console.error(e);
      this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.ASSISTANT_UPDATE_ERROR');
    } finally {
      this.saving.set(false);
    }
  }

  protected back(): void {
    this.pageService.navigateToPrevious(FALLBACK_BACK_URL, this.route);
  }

  canDeactivate(): boolean {
    return !this.assistantForm.dirty;
  }
}
